import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Users,
  Shield,
  Search,
  UserPlus,
  Mail,
  Clock,
  Edit3,
  CheckCircle,
  PauseCircle,
  Archive,
  RefreshCw,
} from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { teamService } from './teamService'
import { DetailedRosterMember, TrackingStatus, OnboardingStatus } from '../../types/team'
import { AddAthleteModal } from './AddAthleteModal'
import { EditAthleteModal } from './EditAthleteModal'
import { TrackingStatusModal } from './TrackingStatusModal'
import { InviteAthleteModal } from './InviteAthleteModal'
import { EvaluationWorkspace } from '../evaluations/EvaluationWorkspace'
import { evaluationCapabilities } from '../evaluations/EvaluationHub'
import { Modal } from '../shared/Modal'

export const TeamView: React.FC = () => {
  const { user } = useAuth()
  const [activeSubTab, setActiveSubTab] = useState<'roster' | 'staff'>('roster')

  // Estado del equipo y temporada
  const defaultOrgId = '11111111-1111-1111-1111-111111111111'
  const defaultTeamId = user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'
  const defaultSeasonId = '44444444-4444-4444-4444-444444444444'

  const [roster, setRoster] = useState<DetailedRosterMember[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [toastMsg, setToastMsg] = useState<string | null>(null)

  // Filtros y búsqueda
  const [searchQuery, setSearchQuery] = useState('')
  const [trackingFilter, setTrackingFilter] = useState<'all' | TrackingStatus>('all')
  const [onboardingFilter, setOnboardingFilter] = useState<'all' | OnboardingStatus>('all')

  // Modales
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectedMemberForEdit, setSelectedMemberForEdit] = useState<DetailedRosterMember | null>(null)
  const [selectedMemberForTracking, setSelectedMemberForTracking] = useState<DetailedRosterMember | null>(null)
  const [selectedMemberForInvite, setSelectedMemberForInvite] = useState<DetailedRosterMember | null>(null)
  const [evaluationAthlete,setEvaluationAthlete]=useState<string|null>(null)
  const canEvaluate=user?.memberships.some(m=>m.is_active&&(!m.team_id||m.team_id===defaultTeamId)&&evaluationCapabilities.some(c=>m.capabilities?.includes(c)))

  const canManageRoster = user?.memberships.some((m) => m.is_active &&
    (!m.team_id || m.team_id === defaultTeamId) &&
    (m.roles.includes('coach') || m.capabilities?.includes('manage_roster'))
  )

  const loadRoster = useCallback(async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      // La consulta incluye temporadas históricas: una ficha con evaluaciones
      // no desaparece por una temporada fija de la demo inicial.
      const data = await teamService.getTeamRosterDetailed(defaultTeamId)
      setRoster(data)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar la plantilla.')
    } finally {
      setLoading(false)
    }
  }, [defaultTeamId, defaultSeasonId])

  useEffect(() => {
    loadRoster()
  }, [loadRoster])

  const showToast = (msg: string) => {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(null), 4000)
  }

  // Filtrado de la plantilla
  const filteredRoster = useMemo(() => {
    return roster.filter((m) => {
      // 1. Búsqueda por texto (nombre, dorsal, posición)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim()
        const matchesName = m.full_name.toLowerCase().includes(q)
        const matchesDorsal = m.jersey_number !== null && String(m.jersey_number).includes(q)
        const matchesPosition =
          m.primary_position.toLowerCase().includes(q) ||
          m.secondary_positions.some((p) => p.toLowerCase().includes(q))
        if (!matchesName && !matchesDorsal && !matchesPosition) {
          return false
        }
      }

      // 2. Filtro de seguimiento
      if (trackingFilter !== 'all' && m.tracking_status !== trackingFilter) {
        return false
      }

      // 3. Filtro de onboarding
      if (onboardingFilter !== 'all' && m.onboarding_status !== onboardingFilter) {
        return false
      }

      return true
    })
  }, [roster, searchQuery, trackingFilter, onboardingFilter])

  // Contadores de seguimiento
  const metrics = useMemo(() => {
    const total = roster.length
    const active = roster.filter((m) => m.tracking_status === 'active').length
    const paused = roster.filter((m) => m.tracking_status === 'paused').length
    const archived = roster.filter((m) => m.tracking_status === 'archived').length
    const linked = roster.filter((m) => m.onboarding_status === 'profile_completed' || m.onboarding_status === 'account_linked').length
    return { total, active, paused, archived, linked }
  }, [roster])

  return (
    <div>
      {/* Cabecera de Equipo */}
      <div
        style={{
          marginBottom: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.2rem', color: 'var(--text-main)' }}>
            Selección y Plantilla
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            Grupo de seguimiento, gestión de incorporaciones, estados y cuerpo técnico.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="badge badge-muted">Temporada 2026 · Flag 5v5</span>
          {canManageRoster && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem' }}
              onClick={() => setAddModalOpen(true)}
            >
              <UserPlus size={15} />
              <span>Añadir Atleta</span>
            </button>
          )}
        </div>
      </div>

      {/* Toast de confirmación */}
      {toastMsg && (
        <div
          role="status"
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--status-success-bg)',
            color: 'var(--status-success-text)',
            border: '1px solid var(--status-success-border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.82rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <CheckCircle size={16} />
          <div>{toastMsg}</div>
        </div>
      )}

      {/* Subnavegación de Sección */}
      <div className="sports-nav-tabs" style={{ marginBottom: '1.25rem' }}>
        <button
          type="button"
          className={`sports-nav-btn ${activeSubTab === 'roster' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('roster')}
        >
          <Users size={16} />
          <span>Plantilla y Seguimiento ({metrics.total})</span>
        </button>

        <button
          type="button"
          className={`sports-nav-btn ${activeSubTab === 'staff' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('staff')}
        >
          <Shield size={16} />
          <span>Cuerpo Técnico y Estructura</span>
        </button>
      </div>

      {activeSubTab === 'roster' ? (
        <div>
          {/* Tarjetas de Resumen de Seguimiento */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <div
              style={{
                backgroundColor: 'var(--surface-primary)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.75rem 1rem',
              }}
            >
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>
                Total Seguidos
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
                {metrics.total}
              </div>
            </div>

            <div
              style={{
                backgroundColor: 'var(--surface-primary)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.75rem 1rem',
              }}
            >
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>
                Seguimiento Activo
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--brand-blue)', marginTop: '0.2rem' }}>
                {metrics.active}
              </div>
            </div>

            <div
              style={{
                backgroundColor: 'var(--surface-primary)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.75rem 1rem',
              }}
            >
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>
                Pausados
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--brand-yellow)', marginTop: '0.2rem' }}>
                {metrics.paused}
              </div>
            </div>

            <div
              style={{
                backgroundColor: 'var(--surface-primary)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.75rem 1rem',
              }}
            >
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>
                Archivados
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                {metrics.archived}
              </div>
            </div>

            <div
              style={{
                backgroundColor: 'var(--surface-primary)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.75rem 1rem',
              }}
            >
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>
                Cuentas Vinculadas
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--status-success-text)', marginTop: '0.2rem' }}>
                {metrics.linked}
              </div>
            </div>
          </div>

          {/* Barra de Filtros y Búsqueda */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1rem',
              alignItems: 'center',
            }}
          >
            {/* Buscador */}
            <div style={{ flex: '1 1 240px', position: 'relative' }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre, dorsal o posición..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: '2.25rem' }}
              />
            </div>

            {/* Selector Seguimiento */}
            <div style={{ flex: '0 0 auto' }}>
              <select
                className="form-control"
                value={trackingFilter}
                onChange={(e) => setTrackingFilter(e.target.value as 'all' | TrackingStatus)}
                style={{ fontSize: '0.82rem' }}
              >
                <option value="all">Seguimiento: Todos</option>
                <option value="active">Solo Activos</option>
                <option value="paused">Solo Pausados</option>
                <option value="archived">Solo Archivados</option>
              </select>
            </div>

            {/* Selector Onboarding */}
            <div style={{ flex: '0 0 auto' }}>
              <select
                className="form-control"
                value={onboardingFilter}
                onChange={(e) => setOnboardingFilter(e.target.value as 'all' | OnboardingStatus)}
                style={{ fontSize: '0.82rem' }}
              >
                <option value="all">Incorporación: Todas</option>
                <option value="no_account">Sin cuenta</option>
                <option value="invitation_pending">Invitación pendiente</option>
                <option value="account_linked">Cuenta vinculada</option>
                <option value="profile_completed">Perfil completo</option>
                <option value="invitation_expired">Invitación caducada</option>
                <option value="invitation_revoked">Invitación revocada</option>
              </select>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={loadRoster}
              disabled={loading}
              title="Recargar plantilla"
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.65rem' }}
            >
              <RefreshCw size={14} className={loading ? 'spin' : ''} />
              <span style={{ fontSize: '0.78rem' }}>Actualizar</span>
            </button>
          </div>

          {/* Tabla de Jugadores */}
          <div className="table-container">
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                Cargando grupo de seguimiento...
              </div>
            ) : errorMsg ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--status-danger-text)' }}>
                {errorMsg}
              </div>
            ) : filteredRoster.length === 0 ? (
              <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <Users size={32} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                <p style={{ margin: 0, fontSize: '0.88rem' }}>
                  No se encontraron atletas con los criterios seleccionados.
                </p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }} className="numeric">Dorsal</th>
                    <th>Jugador</th>
                    <th>Posición</th>
                    <th>Unidad</th>
                    <th>Seguimiento</th>
                    <th>Incorporación / Cuenta</th>
                    {canManageRoster && <th style={{ width: '130px', textAlign: 'right' }}>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredRoster.map((m) => (
                    <tr key={m.roster_entry_id}>
                      <td className="numeric" style={{ fontWeight: 700 }}>
                        {m.jersey_number !== null ? `#${m.jersey_number}` : '—'}
                      </td>

                      <td style={{ fontWeight: 600 }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ color: 'var(--text-main)' }}>{m.full_name}</span>
                          {m.user_email && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                              {m.user_email}
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <span className="badge badge-subtle" style={{ fontWeight: 700, marginRight: '4px' }}>
                          {m.primary_position}
                        </span>
                        {m.secondary_positions && m.secondary_positions.length > 0 && (
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                            {m.secondary_positions.join(', ')}
                          </span>
                        )}
                      </td>

                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor:
                              m.unit === 'offense'
                                ? 'rgba(37, 99, 235, 0.12)'
                                : m.unit === 'defense'
                                ? 'rgba(239, 68, 68, 0.12)'
                                : 'rgba(234, 179, 8, 0.12)',
                            color:
                              m.unit === 'offense'
                                ? 'var(--brand-blue)'
                                : m.unit === 'defense'
                                ? 'var(--brand-red)'
                                : 'var(--brand-yellow)',
                            fontSize: '0.73rem',
                          }}
                        >
                          {m.unit === 'offense' ? 'Ataque' : m.unit === 'defense' ? 'Defensa' : 'Ambas'}
                        </span>
                      </td>

                      <td>
                        {m.tracking_status === 'active' && (
                          <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            <CheckCircle size={11} />
                            <span>Activo</span>
                          </span>
                        )}
                        {m.tracking_status === 'paused' && (
                          <span className="badge badge-yellow" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            <PauseCircle size={11} />
                            <span>Pausado</span>
                          </span>
                        )}
                        {m.tracking_status === 'archived' && (
                          <span className="badge badge-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            <Archive size={11} />
                            <span>Archivado</span>
                          </span>
                        )}
                      </td>

                      <td>
                        {m.onboarding_status === 'profile_completed' && (
                          <span className="badge badge-success">Perfil Completo</span>
                        )}
                        {m.onboarding_status === 'account_linked' && (
                          <span className="badge badge-info">Cuenta Vinculada · Perfil Pendiente</span>
                        )}
                        {m.onboarding_status === 'invitation_pending' && (
                          <span className="badge badge-yellow" title="Invitación enviada pendiente de activación">
                            Invitación Pendiente
                          </span>
                        )}
                        {m.onboarding_status === 'invitation_expired' && (
                          <span className="badge badge-red" title="La invitación previa caducó">
                            Invitación Caducada
                          </span>
                        )}
                        {m.onboarding_status === 'invitation_revoked' && (
                          <span className="badge badge-muted">Invitación Revocada</span>
                        )}
                        {m.onboarding_status === 'no_account' && (
                          <span className="badge badge-muted">Sin cuenta</span>
                        )}
                      </td>

                      {canManageRoster && (
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.35rem' }}>
                            {canEvaluate&&<button className="btn btn-secondary" onClick={()=>setEvaluationAthlete(m.athlete_id)}>Evaluaciones</button>}
                            {/* Invitar / Reenviar */}
                            {!m.user_id && (
                              <button
                                type="button"
                                className="btn btn-ghost"
                                style={{ padding: '0.3rem', color: 'var(--brand-blue)' }}
                                title={m.onboarding_status === 'invitation_pending' ? 'Reenviar invitación' : 'Invitar atleta'}
                                onClick={() => setSelectedMemberForInvite(m)}
                              >
                                <Mail size={15} />
                              </button>
                            )}

                            {/* Editar ficha deportiva */}
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{ padding: '0.3rem' }}
                              title="Editar ficha deportiva"
                              onClick={() => setSelectedMemberForEdit(m)}
                            >
                              <Edit3 size={15} />
                            </button>

                            {/* Gestión de seguimiento */}
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{ padding: '0.3rem' }}
                              title="Cambiar estado de seguimiento (pausar/reanudar/archivar)"
                              onClick={() => setSelectedMemberForTracking(m)}
                            >
                              <Clock size={15} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        /* Pestaña de Cuerpo Técnico y Estructura */
        <div>
          <div className="table-container" style={{ marginBottom: '1.25rem' }}>
            <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={16} style={{ color: 'var(--brand-red)' }} />
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600 }}>Cuerpo Técnico Oficial</h3>
              </div>
              <span className="badge badge-muted">4 Miembros</span>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Rol Principal</th>
                  <th>Área de Responsabilidad</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 600 }}>Christian</td>
                  <td><span className="badge badge-red">Head Coach</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>Coordinación general, sistema ofensivo y guion</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Alicia</td>
                  <td><span className="badge badge-yellow">Coordinadora Defensiva</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>Estrategia defensiva, coberturas y blitz</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Gonzalo</td>
                  <td><span className="badge badge-info">Preparador Físico</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>Preparación atlética, carga sRPE y asistencia defensiva</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Carlos</td>
                  <td><span className="badge badge-success">Salud / Fisio</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>Fisioterapia, valoración sanitaria y restricciones</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="table-container">
            <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600 }}>Distribución Táctica Canónica Flag 5v5</h3>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Unidad</th>
                  <th>Posición</th>
                  <th style={{ width: '90px' }} className="numeric">Cupo Mín.</th>
                  <th>Funciones Tácticas</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td rowSpan={4} style={{ fontWeight: 700, verticalAlign: 'top', backgroundColor: 'var(--surface-subtle)', borderRight: '1px solid var(--surface-border)' }}>
                    Ataque
                  </td>
                  <td style={{ fontWeight: 600 }}>Quarterback (QB)</td>
                  <td className="numeric" style={{ fontWeight: 700 }}>2</td>
                  <td style={{ color: 'var(--text-secondary)' }}>Dirección ofensiva y lanzamiento primario</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Center (C)</td>
                  <td className="numeric" style={{ fontWeight: 700 }}>2</td>
                  <td style={{ color: 'var(--text-secondary)' }}>Snap inicial y rutas de recepción interior</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Slot Receiver</td>
                  <td className="numeric" style={{ fontWeight: 700 }}>2</td>
                  <td style={{ color: 'var(--text-secondary)' }}>Rutas intermedias y desmarques rápidos</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Wide Receiver (WR)</td>
                  <td className="numeric" style={{ fontWeight: 700 }}>4</td>
                  <td style={{ color: 'var(--text-secondary)' }}>Profundidad exterior y recepciones en banda</td>
                </tr>

                <tr>
                  <td rowSpan={3} style={{ fontWeight: 700, verticalAlign: 'top', backgroundColor: 'var(--surface-subtle)', borderRight: '1px solid var(--surface-border)' }}>
                    Defensa
                  </td>
                  <td style={{ fontWeight: 600 }}>Blitzer (RUSH)</td>
                  <td className="numeric" style={{ fontWeight: 700 }}>2</td>
                  <td style={{ color: 'var(--text-secondary)' }}>Presión directa al quarterback rival a 7 yardas</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Linebacker (LB)</td>
                  <td className="numeric" style={{ fontWeight: 700 }}>4</td>
                  <td style={{ color: 'var(--text-secondary)' }}>Zona corta, lectura de pases y carreras</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Cornerback (CB)</td>
                  <td className="numeric" style={{ fontWeight: 700 }}>4</td>
                  <td style={{ color: 'var(--text-secondary)' }}>Cobertura profunda y marcaje exterior</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modales */}
      <AddAthleteModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={(msg) => {
          showToast(msg)
          loadRoster()
        }}
        organizationId={defaultOrgId}
        teamId={defaultTeamId}
        seasonId={defaultSeasonId}
      />

      <EditAthleteModal
        isOpen={!!selectedMemberForEdit}
        onClose={() => setSelectedMemberForEdit(null)}
        onSuccess={(msg) => {
          showToast(msg)
          loadRoster()
        }}
        member={selectedMemberForEdit}
      />

      <TrackingStatusModal
        isOpen={!!selectedMemberForTracking}
        onClose={() => setSelectedMemberForTracking(null)}
        onSuccess={(msg) => {
          showToast(msg)
          loadRoster()
        }}
        member={selectedMemberForTracking}
      />

      <InviteAthleteModal
        isOpen={!!selectedMemberForInvite}
        onClose={() => setSelectedMemberForInvite(null)}
        onSuccess={(msg) => {
          showToast(msg)
          loadRoster()
        }}
        member={selectedMemberForInvite}
        organizationId={defaultOrgId}
        teamId={defaultTeamId}
      />
      <Modal isOpen={Boolean(evaluationAthlete)} onClose={()=>setEvaluationAthlete(null)} title="Jugador · Evaluaciones" size="lg">
        {evaluationAthlete&&<EvaluationWorkspace teamId={defaultTeamId} athleteId={evaluationAthlete}/>}
      </Modal>
    </div>
  )
}
